import { useMemo } from 'react'

import { ITemplatePropertyInput } from '~/client/graph/types.generated'
import { getResourceDefinition } from '~/client/src/shared/resources/definitions'
import type { ResourceSchema } from '~/client/src/shared/resources/domain/resourceSchema'
import { classifyResourcePropertyInput } from '~/client/src/shared/resources/resourcePropertyInput'

import { PropertyData, sortFormProperties } from './formProperties'

export const useFormPropertiesData = ({
  templateId,
  resourceTypeId,
  resourceSchema,
}: {
  templateId: string | null
  resourceTypeId?: string
  resourceSchema: ResourceSchema
}) => {
  const mandatoryPropertyKeys =
    getResourceDefinition(resourceTypeId)?.form?.mandatoryPropertyKeys
  const template = resourceSchema.templates.find(item => item.id === templateId)

  const { properties, savedProperties } = useMemo(() => {
    const liveProperties = resourceSchema.properties
    const templateProperties = template?.properties ?? []

    const templatePropById = new Map(
      templateProperties.map(tp => [tp.propertyId, tp] as const),
    )

    const maxSavedOrderIndex = templateProperties.reduce(
      (max, tp) => Math.max(max, tp.orderIndex ?? 0),
      -1,
    )

    const pickableLiveProperties = liveProperties.filter(
      property =>
        classifyResourcePropertyInput(property).kind === 'editable-field',
    )
    const visible: PropertyData[] = pickableLiveProperties.map(
      (live, fallbackIndex) => {
        const saved = templatePropById.get(live.id)
        // A template saved before the property became mandatory can have it hidden or
        // absent; the declaration wins, so an existing form gains it on next edit.
        const isLocked = !!mandatoryPropertyKeys?.includes(live.name || '')
        return {
          propertyId: live.id,
          label: live.displayName || '',
          name: live.name || '',
          type: live.propertyType,
          configuration: live.configuration,
          required: isLocked || (saved?.isRequired ?? false),
          isHidden: isLocked ? false : saved ? !!saved.isHidden : true,
          isLocked,
          orderIndex:
            saved?.orderIndex ?? maxSavedOrderIndex + 1 + fallbackIndex,
        }
      },
    )

    const saved: ITemplatePropertyInput[] = templateProperties.map(tp => ({
      propertyId: tp.propertyId,
      orderIndex: tp.orderIndex ?? 0,
      isRequired: tp.isRequired ?? false,
      isHidden: tp.isHidden ?? false,
      defaultValue: tp.defaultValue ?? null,
    }))

    return {
      properties: sortFormProperties(visible),
      savedProperties: saved,
    }
  }, [mandatoryPropertyKeys, resourceSchema.properties, template])

  return {
    templateName: template?.name || '',
    properties,
    savedProperties,
    loading: resourceSchema.loading,
  }
}
